import type { PayloadAction } from '@reduxjs/toolkit';
import { editor } from 'Cloud/Application/Editor';
import config from 'Cloud/config';
import { ALL_DOCUMENTS_FOLDER_ID, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { getIncomingPublicCategory } from 'reactApp/modules/incomingPublic/incomingPublic.selectors';
import { EIncomingPublicCategory } from 'reactApp/modules/incomingPublic/incomingPublic.types';
import { createFamilyAlbumAction, createFamilyFolderAction, createFolderAction } from 'reactApp/modules/modifying/modifying.actions';
import { createError } from 'reactApp/modules/modifying/modifying.constants';
import type { ICreateNewItem } from 'reactApp/modules/modifying/modifying.types';
import { CSAT_TRIGGER_EVENTS, pushCSATTriggerEvent } from 'reactApp/modules/promo/sagas/initCSATSurvey';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { isDomainFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getCurrentFolder as getCurrentFolderAction, hasParentsDomainFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { renderSelectFolderDialog } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.toolkit';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { channel } from 'redux-saga';
import { select, take } from 'redux-saga/effects';
import { put } from 'typed-redux-saga';

import { createFolderRequest } from './createFolder.saga';

const MAX_PATH_SIZE = 1024 - config.get('ITEM_NAME_MAX_LENGTH');

function assertMaxFoldersPath(folderPath: string, maxPathSize = MAX_PATH_SIZE) {
    return new TextEncoder().encode(folderPath).length > maxPathSize;
}

// eslint-disable-next-line max-lines-per-function, complexity
export function* handleCreateNewItem(action: PayloadAction<ICreateNewItem>) {
    const { type, from, showShareOnlyButton, gaPrefix = '', inHomeRoot = false, createdInToolbar } = action.payload;
    let showShareButton = action.payload.showShareButton;

    const isFolder = type === 'folder';
    const isFamilyFolder = type === 'familyFolder';
    const isFamilyAlbum = type === 'familyAlbum';

    const currentStorage = yield select(getCurrentStorage);
    const { isSearch, isSharedLinks, isTrashBin, isHome, isStart, isAllDocuments, isIncomingPublic, isFamilyFolders, isFamilyAlbums } =
        getStorage(currentStorage);
    const folder = yield select(getCurrentFolderAction);
    const hasParentsDmnFolder = yield select(hasParentsDomainFolder, folder);

    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const isOverquota = yield select(UserQuotaSelectors.isOverquota);

    const ignoreUserOverquota = isDomainFolder(folder) || hasParentsDmnFolder || isCorpUser;

    const incomingPublicCategory = yield select(getIncomingPublicCategory);

    const isNewbie = yield select(UserSelectors.isNewbie);
    if (isNewbie) {
        return;
    }

    if (isOverquota && !ignoreUserOverquota) {
        openDisabledFeaturePopupHelper({ disabledFeature: isFolder ? DisabledFeature.newFolder : DisabledFeature.newFile });
        return;
    }

    let currentFolder = ROOT_FOLDER_ID;

    if (isHome) {
        currentFolder = folder?.id && folder?.id !== ROOT_FOLDER_ID ? folder?.id : ROOT_FOLDER_ID;
    } else if (inHomeRoot || isStart || isFamilyFolders || isFamilyAlbums) {
        currentFolder = ROOT_FOLDER_ID;
    } else if (isAllDocuments || (isIncomingPublic && incomingPublicCategory === EIncomingPublicCategory.allDocuments)) {
        currentFolder = `/${ALL_DOCUMENTS_FOLDER_ID}`;

        const documentsFolder: ReturnType<typeof getHomeItemById> = yield select(getHomeItemById, currentFolder);

        if (!documentsFolder) {
            yield createFolderRequest({
                folderName: ALL_DOCUMENTS_FOLDER_ID,
                share: false,
                parent: ROOT_FOLDER_ID,
                storage: EStorageType.home,
                gaPrefix: 'docs',
            });
        }
    } else {
        showShareButton = false;
        const selectChannel = channel();
        renderSelectFolderDialog({
            title: 'Где создать?',
            onSuccess: (answer) => {
                if (answer.action === 'create') {
                    currentFolder = answer.destination;
                    selectChannel.put(true);
                    return;
                }
                selectChannel.close();
            },
        });

        yield take(selectChannel);
        selectChannel.close();
    }

    const storage = isSearch || isSharedLinks || isTrashBin || isStart || isHome ? EStorageType.home : undefined;

    if (isFolder) {
        // tempexp-14737-next-line
        const createChannel = channel();

        sendPaymentGa({
            eventCategory: ECategoryGa.created,
            action: 'created-folder',
            type_folder: showShareOnlyButton ? 'common-folder' : 'folder',
            source: from,
        });

        yield put(
            createFolderAction({
                parentFolderId: currentFolder,
                storage,
                showCreateButton: !showShareOnlyButton,
                showShareButton,
                gaPrefix,
                // tempexp-14737-start
                onCreateFolder: createChannel.put,
                createdInToolbar,
            })
        );

        createChannel.close();
        // tempexp-14737-end
        return;
    }

    if (isFamilyFolder) {
        const createChannel = channel();

        sendPaymentGa({
            eventCategory: ECategoryGa.created,
            action: 'created-folder',
            type_folder: 'family-folder',
            source: from,
        });

        yield put(
            createFamilyFolderAction({
                parentFolderId: currentFolder,
                storage,
                showCreateButton: !showShareOnlyButton,
                showShareButton,
                gaPrefix,
                onCreateFolder: createChannel.put,
                createdInToolbar,
            })
        );

        createChannel.close();
        return;
    }

    if (isFamilyAlbum) {
        const createChannel = channel();

        yield put(
            createFamilyAlbumAction({
                parentFolderId: currentFolder,
                storage,
                showCreateButton: !showShareOnlyButton,
                showShareButton,
                gaPrefix,
                onCreateFolder: createChannel.put,
                createdInToolbar,
            })
        );

        createChannel.close();
        return;
    }

    if (assertMaxFoldersPath(currentFolder, MAX_PATH_SIZE)) {
        yield put(
            showSnackbarAction({
                id: 'create-error-path-too-long',
                type: SnackbarTypes.failure,
                text: createError.getMessage('path_too_long'),
                closable: true,
            })
        );
        return;
    }

    editor.createNew(type, null, currentFolder, from);

    sendPaymentGa({
        eventCategory: ECategoryGa.created,
        action: 'created-files',
        type_file: type,
        source: from,
    });

    yield put(pushCSATTriggerEvent({ type: CSAT_TRIGGER_EVENTS.createFile }));
}
